import {
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  Button,
  ListItemButton,
  Collapse,
} from "@mui/material";
import { DefaultTFuncReturn, t } from "i18next";
import { useState } from "react";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PasswordIcon from "@mui/icons-material/Password";
import { useLoginContext } from "../context/LoginContext";
import { useSnackbar } from "../hooks/useSnackbar";

const ChangePassword: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [{ password, confirmPassword, error }, setData] = useState<{
    password: string;
    confirmPassword: string;
    error?: DefaultTFuncReturn;
  }>({ password: "", confirmPassword: "" });

  const { setNewPassword } = useLoginContext();
  const snackbar = useSnackbar();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setData((s) => ({
        ...s,
        error: t("settings.password-mismatch"),
      }));
      return;
    }

    const changed = await setNewPassword(password);

    if (changed) {
      snackbar.open(t("settings.password-changed-success"), "success");
      setOpen(false);
      setData({
        password: "",
        confirmPassword: "",
        error: undefined,
      });
    } else {
      setData((s) => ({
        ...s,
        error: t("settings.password-change-failed"),
      }));
    }
  };

  return (
    <>
      <ListItemButton disableGutters onClick={() => setOpen(!open)}>
        <ListItemIcon>
          <PasswordIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t("settings.password-change-label")} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse
        component="form"
        onSubmit={onSubmit}
        in={open}
        timeout="auto"
        unmountOnExit
      >
        <TextField
          autoComplete="password"
          error={!!error}
          label={t("settings.password-new-label")}
          margin="dense"
          required
          type="password"
          value={password}
          onChange={(e) =>
            setData((d) => ({
              ...d,
              password: e.target.value || "",
              error: undefined,
            }))
          }
        />
        <TextField
          autoComplete="confirm-password"
          error={!!error}
          label={t("settings.password-confirm-label")}
          margin="dense"
          required
          type="password"
          value={confirmPassword}
          onChange={(e) =>
            setData((d) => ({
              ...d,
              confirmPassword: e.target.value || "",
              error: undefined,
            }))
          }
        />
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
        <Button
          type="submit"
          fullWidth
          sx={{ mt: 2 }}
          disabled={!!error || !password || !confirmPassword}
        >
          {t("settings.password-change-label")}
        </Button>
      </Collapse>
    </>
  );
};

export default ChangePassword;
